/**
 * V2 Step 4 — Schedule.
 * Design from v2-screens-2.jsx:
 *  - "Bookings paused" banner
 *  - Start date input
 *  - StatChip with useTween + useChangeFlash animated counters
 *  - Day list as a single white card with per-row border-bottom separators
 *  - Day toggle buttons (Mon/Tue/etc label pill) + TimeInput (native <input type="time">)
 *  - Bookable hours summary section
 * At least one enabled day required.
 */
import * as React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { V2C, fontBody, fontDisplay } from '../shared/tokens';
import { V2Btn, V2SectionWarning } from '../shared/primitives';
import V2Step from '../shared/V2Step';
import { V2_DAYS, type V2DayKey } from '@/components/onboarding/onboardingDefaults';
import type { BusinessSignupData } from '@/components/onboarding/magic-steps/BusinessSignup';

interface Props {
  formData: BusinessSignupData;
  update: (patch: Partial<BusinessSignupData>) => void;
  onBack?: () => void;
  onNext: () => void;
  stepIdx: number;
  totalSteps: number;
  animDir: 'forward' | 'back';
  validationError: string;
}

// Convert "HH:MM" 24hr → "9:00am" / "6:30pm"
function to12hr(t: string): string {
  const [hStr, mStr] = (t || '0:00').split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr || '0', 10);
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, '0')}${ampm}`;
}

// Parse "HH:MM" → minutes since midnight
function toMins(t: string): number {
  const [h, m] = (t || '0:00').split(':').map(Number);
  return h * 60 + m;
}

// ── Animated counter ──────────────────────────────────────────────────────────
function useTween(value: number, duration = 520): number {
  const [v, setV] = useState(value);
  const fromRef = useRef(value);
  useEffect(() => {
    const from = fromRef.current;
    const to = value;
    if (from === to) return; // no change needed
    const t0 = performance.now();
    let raf: number;
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      const e = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setV(from + (to - from) * e);
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return v;
}

function useChangeFlash(value: number, duration = 360): boolean {
  const [flash, setFlash] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current !== value) {
      prev.current = value;
      // Use rAF to avoid synchronous setState-in-effect lint warning
      const id = requestAnimationFrame(() => setFlash(true));
      const t = setTimeout(() => setFlash(false), duration);
      return () => { cancelAnimationFrame(id); clearTimeout(t); };
    }
  }, [value, duration]);
  return flash;
}

function StatChip({ value, label, color, decimals = 0 }: {
  value: number; label: string; color: string; decimals?: number;
}) {
  const tweened = useTween(value);
  const flash = useChangeFlash(value);
  const display = decimals > 0 ? tweened.toFixed(decimals) : Math.round(tweened);
  return (
    <div style={{
      padding: '8px 12px', borderRadius: 10, background: '#fff',
      border: `1px solid ${flash ? color : V2C.line}`, flex: 1, minWidth: 0,
      display: 'flex', alignItems: 'baseline', gap: 6,
      transition: 'border-color 0.25s, box-shadow 0.25s',
      boxShadow: flash ? `0 0 0 3px ${color}22` : 'none',
    }}>
      <div style={{
        fontFamily: fontDisplay, fontSize: 22, lineHeight: 1, color,
        fontVariantNumeric: 'tabular-nums',
        transform: flash ? 'translateY(-1px) scale(1.06)' : 'translateY(0) scale(1)',
        transition: 'transform 0.32s cubic-bezier(.2,.8,.2,1.2)',
        display: 'inline-block',
      }}>{display}</div>
      <div style={{
        fontSize: 10, color: V2C.mute, fontWeight: 800,
        textTransform: 'uppercase', letterSpacing: 0.6, fontFamily: fontBody,
      }}>{label}</div>
    </div>
  );
}

function TimeInput({ value, onChange, onboardingField }: {
  value: string;
  onChange: (v: string) => void;
  onboardingField?: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type="time"
      value={value}
      data-onboarding-field={onboardingField}
      onChange={e => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        flex: 1, minWidth: 0,
        border: `1.5px solid ${focused ? V2C.brand : V2C.line}`,
        borderRadius: 9, padding: '7px 10px',
        fontFamily: fontBody, fontSize: 13, fontWeight: 600, color: V2C.ink,
        background: '#fff', outline: 'none',
        boxShadow: focused ? `0 0 0 3px ${V2C.brandSoft}` : 'none',
        transition: 'all 0.15s',
      }}
    />
  );
}

export default function V2ScheduleStep({ formData, update, onBack, onNext, stepIdx, totalSteps, animDir }: Props) {
  const hours = useMemo(() => formData.workingHours ?? {}, [formData.workingHours]);

  const enabledDays = V2_DAYS.filter(({ key }) => hours[key as V2DayKey]?.enabled);
  const noneOpen = enabledDays.length === 0;

  // Total weekly hours
  const totalHours = useMemo(() => {
    let acc = 0;
    V2_DAYS.forEach(({ key }) => {
      const d = hours[key as V2DayKey];
      if (!d?.enabled) return;
      const span = toMins(d.endTime ?? '17:00') - toMins(d.startTime ?? '09:00');
      acc += Math.max(0, span);
    });
    return acc / 60;
  }, [hours]);

  function toggle(key: V2DayKey) {
    const d = hours[key] ?? { enabled: false, startTime: '09:00', endTime: '17:00' };
    update({ workingHours: { ...hours, [key]: { ...d, enabled: !d.enabled } } });
  }

  function setTime(key: V2DayKey, field: 'startTime' | 'endTime', val: string) {
    const d = hours[key] ?? { enabled: false, startTime: '09:00', endTime: '17:00' };
    update({ workingHours: { ...hours, [key]: { ...d, [field]: val } } });
  }

  return (
    <V2Step
      stepIdx={stepIdx}
      totalSteps={totalSteps}
      title="Your schedule"
      subtitle="Set your weekly hours and when bookings start. Figgy won't accept bookings until your hours are confirmed."
      onBack={onBack}
      animDir={animDir}
    >
      {/* Bookings paused banner */}
      <div style={{
        background: V2C.brandSofter, border: `1px solid ${V2C.line}`, borderRadius: 12,
        padding: '10px 12px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <div style={{
          width: 24, height: 24, borderRadius: 99, background: V2C.brand, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <circle cx="6" cy="6" r="5" stroke="#fff" strokeWidth="1.4"/>
            <path d="M6 3v3l2 1" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </div>
        <div style={{ flex: 1, fontSize: 12, color: V2C.ink, lineHeight: 1.5, fontFamily: fontBody }}>
          <strong>Bookings are paused</strong> until you confirm your hours. Existing clients won't see any booking slots.
        </div>
      </div>

      {/* Start date */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: V2C.mute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6, fontFamily: fontBody }}>
          Accepting bookings from
        </div>
        <input
          type="date"
          value={formData.startDate}
          data-onboarding-field="schedule_start_date"
          onChange={e => update({ startDate: e.target.value })}
          min={new Date().toISOString().split('T')[0]}
          style={{
            width: '100%', padding: '13px 14px', borderRadius: 12, boxSizing: 'border-box',
            border: `1.5px solid ${V2C.line}`, background: '#fff', color: V2C.ink,
            fontFamily: fontBody, fontSize: 15, fontWeight: 600, outline: 'none',
          }}
        />
      </div>

      {/* Stat chips */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, minWidth: 0 }}>
        <StatChip
          value={enabledDays.length}
          label={enabledDays.length === 1 ? 'day open' : 'days open'}
          color={V2C.brand}
        />
        <StatChip
          value={totalHours}
          decimals={1}
          label="hrs / week"
          color={V2C.accent}
        />
      </div>

      {/* Warning */}
      {noneOpen && (
        <V2SectionWarning title="No working days selected">
          Toggle at least one day on, or your booking page won't show any availability.
        </V2SectionWarning>
      )}

      {/* Day list — single white card */}
      <div style={{
        background: '#fff', borderRadius: 18, padding: 6,
        border: `1px solid ${V2C.line}`, marginBottom: 14,
      }}>
        {V2_DAYS.map(({ key, short, label }, i) => {
          const d = hours[key as V2DayKey];
          const enabled = d?.enabled ?? false;
          const start = d?.startTime ?? '09:00';
          const end = d?.endTime ?? '17:00';
          return (
            <div key={key} style={{
              padding: 0,
              borderBottom: i < V2_DAYS.length - 1 ? `1px solid ${V2C.line}` : 'none',
              background: enabled ? `${V2C.brandSoft}50` : 'transparent',
              borderRadius: i === 0 ? '12px 12px 0 0' : i === V2_DAYS.length - 1 ? '0 0 12px 12px' : 0,
              transition: 'background 0.2s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 6px', minWidth: 0 }}>
                {/* Day toggle button */}
                <button
                  onClick={() => toggle(key as V2DayKey)}
                  aria-label={`Toggle ${label}`}
                  style={{
                    width: 56, height: 32, borderRadius: 9, border: 'none',
                    background: enabled ? V2C.brand : V2C.bgAlt,
                    color: enabled ? '#fff' : V2C.mute,
                    fontFamily: fontBody, fontSize: 12, fontWeight: 700,
                    cursor: 'pointer', letterSpacing: 0.3, flexShrink: 0,
                    boxShadow: enabled ? `0 2px 0 ${V2C.brandDark}` : 'none',
                    transition: 'all 0.18s', padding: 0,
                  }}
                >
                  {short}
                </button>

                {enabled ? (
                  <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center', minWidth: 0 }}>
                    <TimeInput
                      value={start}
                      onChange={v => setTime(key as V2DayKey, 'startTime', v)}
                      onboardingField={`schedule_${key}_start_time`}
                    />
                    <span style={{ color: V2C.mute, fontSize: 12, fontWeight: 700, flexShrink: 0, fontFamily: fontBody }}>–</span>
                    <TimeInput
                      value={end}
                      onChange={v => setTime(key as V2DayKey, 'endTime', v)}
                      onboardingField={`schedule_${key}_end_time`}
                    />
                  </div>
                ) : (
                  <div style={{
                    flex: 1, fontSize: 14, color: V2C.muteSoft,
                    fontFamily: fontDisplay, fontStyle: 'italic', minWidth: 0,
                  }}>
                    Closed
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bookable hours summary */}
      {!noneOpen && (
        <div style={{
          background: '#fff', borderRadius: 14, border: `1px solid ${V2C.line}`,
          padding: 14, marginBottom: 18,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase',
            color: V2C.mute, marginBottom: 8, fontFamily: fontBody,
          }}>
            Bookable hours summary
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {V2_DAYS.map(({ key, label }) => {
              const d = hours[key as V2DayKey];
              const on = d?.enabled ?? false;
              return (
                <div key={key} style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 13, color: on ? V2C.ink : V2C.muteSoft,
                  fontFamily: fontBody, padding: '2px 0',
                }}>
                  <span style={{ fontWeight: 600 }}>{label}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {on ? `${to12hr(d!.startTime ?? '09:00')} – ${to12hr(d!.endTime ?? '17:00')}` : 'Closed'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ position: 'sticky', bottom: 0, paddingBottom: 24, paddingTop: 8, background: 'linear-gradient(to bottom, transparent, #F3F3FF 60%)' }}>
        <V2Btn onClick={onNext} disabled={noneOpen}>
          {noneOpen ? 'Add a working day to continue' : 'Confirm hours & continue'}
        </V2Btn>
      </div>
    </V2Step>
  );
}
